import React, { useState, useEffect, useRef } from "react";
import { Typography, Empty, Pagination, Button } from "antd";
import {
  CheckOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./styles/NotificationsModal.css";
import TailSpinLoader from "../Items/Loaders/TailSpinLoader";
import { formatDate } from "../../helpers/date";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";

export interface NotificationItem {
  _id: string;
  notificationTitle: string;
  notificationMessage: string;
  notificationType: string;
  notificationSenderName: string;
  notificationSenderRole: string;
  createdAt: string;
  notificationReceivers: Array<{
    receiverId: number;
    viewed: boolean;
    notified: boolean;
  }>;
}

export interface NotificationsResponseData {
  currentPage: number;
  totalPages: number;
  nextPage: number | null;
  previousPage: number | null;
  totalCount: number;
  unreadCount: number;
  pageSize: number;
  notifications: NotificationItem[];
}

interface NotificationsDropdownProps {
  notifications: NotificationItem[] | null;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onClose?: () => void;
  onNotificationRead?: () => void;
  currentStaffId?: number;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  notifications,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onClose,
  onNotificationRead,
  currentStaffId,
}) => {
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);

  const openNotificationRef = useRef(openNotification);
  const onNotificationReadRef = useRef(onNotificationRead);

  useEffect(() => {
    openNotificationRef.current = openNotification;
  }, [openNotification]);

  useEffect(() => {
    onNotificationReadRef.current = onNotificationRead;
  }, [onNotificationRead]);

  const [
    postMarkAsReadApi,
    markAsReadResponse,
    markAsReadError,
    markAsReadLoading,
    markAsReadMessage,
    markAsReadErrorCount,
  ] = useAxiosPost<ResponseProp & { notificationId: string }>(
    "notifications/markAsRead"
  );

  const [
    postMarkAllAsReadApi,
    markAllAsReadResponse,
    markAllAsReadError,
    markAllAsReadLoading,
    markAllAsReadMessage,
    markAllAsReadErrorCount,
  ] = useAxiosPost<ResponseProp>("notifications/markAllAsRead");

  const [
    postMarkAllAsUnreadApi,
    markAllAsUnreadResponse,
    markAllAsUnreadError,
    markAllAsUnreadLoading,
    markAllAsUnreadMessage,
    markAllAsUnreadErrorCount,
  ] = useAxiosPost<ResponseProp>("notifications/markAllAsUnread");

  const getNotificationTypeColor = (type: string): string => {
    const typeColors: Record<string, string> = {
      info: "#1890ff",
      success: "#52c41a",
      warning: "#faad14",
      error: "#ff4d4f",
      default: "#722ed1",
    };
    return typeColors[type.toLowerCase()] || typeColors.default;
  };

  const handleNotificationClick = (
    notificationId: string,
    isViewed: boolean
  ) => {
    // Mark as read if unread
    if (!isViewed) {
      postMarkAsReadApi({ notificationId });
    }
    navigate("/action-queue");
    if (onClose) {
      onClose();
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    setMarkingAsRead(notificationId);
    postMarkAsReadApi({ notificationId });
  };

  useEffect(() => {
    if (markAsReadResponse !== null) {
      openNotificationRef.current(
        "success",
        "Success",
        "Notification marked as read"
      );
      setMarkingAsRead(null);
      // Wait for response, add delay, then refresh notifications
      setTimeout(() => {
        if (onNotificationReadRef.current) {
          onNotificationReadRef.current();
        }
      }, 500); // 500ms delay after successful response
    }
  }, [markAsReadResponse]);

  useEffect(() => {
    if (markAsReadError !== null && markAsReadErrorCount !== 0) {
      openNotificationRef.current("error", "Operation Failed", markAsReadError);
      setMarkingAsRead(null);
    }
  }, [markAsReadError, markAsReadErrorCount]);

  const handleMarkAllAsRead = () => {
    postMarkAllAsReadApi({});
  };

  const handleMarkAllAsUnread = () => {
    postMarkAllAsUnreadApi({});
  };

  useEffect(() => {
    if (markAllAsReadResponse !== null) {
      openNotificationRef.current(
        "success",
        "Success",
        "All notifications marked as read"
      );
      // Wait for response, add delay, then refresh notifications
      setTimeout(() => {
        if (onNotificationReadRef.current) {
          onNotificationReadRef.current();
        }
      }, 500); // 500ms delay after successful response
    }
  }, [markAllAsReadResponse]);

  useEffect(() => {
    if (markAllAsReadError !== null && markAllAsReadErrorCount !== 0) {
      openNotificationRef.current(
        "error",
        "Operation Failed",
        markAllAsReadError
      );
    }
  }, [markAllAsReadError, markAllAsReadErrorCount]);

  useEffect(() => {
    if (markAllAsUnreadResponse !== null) {
      openNotificationRef.current(
        "success",
        "Success",
        "All notifications marked as unread"
      );
      // Wait for response, add delay, then refresh notifications
      setTimeout(() => {
        if (onNotificationReadRef.current) {
          onNotificationReadRef.current();
        }
      }, 500); // 500ms delay after successful response
    }
  }, [markAllAsUnreadResponse]);

  useEffect(() => {
    if (markAllAsUnreadError !== null && markAllAsUnreadErrorCount !== 0) {
      openNotificationRef.current(
        "error",
        "Operation Failed",
        markAllAsUnreadError
      );
    }
  }, [markAllAsUnreadError, markAllAsUnreadErrorCount]);

  return (
    <>
      {contextHolder}
      <div className="notifications-dropdown">
        <div className="notifications-dropdown-header">
          <Typography className="notifications-dropdown-title">
            Notifications
          </Typography>
          <div className="notifications-header-actions">
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={handleMarkAllAsRead}
              loading={markAllAsReadLoading}
              className="mark-all-btn"
              title="Mark all as read"
            >
              Mark All Read
            </Button>
            {/* <Button
              type="text"
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={handleMarkAllAsUnread}
              loading={markAllAsUnreadLoading}
              className="mark-all-btn"
              title="Mark all as unread"
            >
              Mark All Unread
            </Button> */}
          </div>
        </div>
        <div className="notifications-dropdown-content">
          {isLoading ? (
            <div className="notifications-loading-container">
              <TailSpinLoader />
            </div>
          ) : notifications && notifications.length > 0 ? (
            <>
              <div className="notifications-list">
                {notifications.map((notification: NotificationItem) => {
                  const receiver = currentStaffId
                    ? notification.notificationReceivers?.find(
                        (r) => r.receiverId === currentStaffId
                      )
                    : notification.notificationReceivers?.[0];
                  const isViewed = receiver?.viewed === true;
                  const isNotified = receiver?.notified === true;
                  const isMarking = markingAsRead === notification._id;
                  return (
                    <div
                      key={notification._id}
                      className={`notification-item ${
                        !isViewed ? "notification-unread" : ""
                      }`}
                    >
                      <div className="notification-header">
                        <div
                          className="notification-type-indicator"
                          style={{
                            backgroundColor: getNotificationTypeColor(
                              notification.notificationType
                            ),
                          }}
                        />
                        <div 
                          className="notification-title-section"
                          style={{
                            paddingRight: isNotified && !isViewed ? "100px" : !isViewed ? "40px" : "8px"
                          }}
                        >
                          <Typography
                            className="notification-title notification-clickable"
                            onClick={() =>
                              handleNotificationClick(
                                notification._id,
                                isViewed
                              )
                            }
                          >
                            {notification.notificationTitle}
                          </Typography>
                          <Typography className="notification-sender">
                            {notification.notificationSenderName} (
                            {notification.notificationSenderRole})
                          </Typography>
                        </div>
                        {!isViewed && (
                          <Button
                            type="text"
                            icon={<CheckOutlined />}
                            className="notification-mark-read-btn"
                            onClick={(e) =>
                              handleMarkAsRead(e, notification._id)
                            }
                            loading={isMarking}
                            title="Mark as read"
                          />
                        )}
                        {isNotified && !isViewed && (
                          <span
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "40px",
                              fontSize: "10px",
                              color: "#8c8c8c",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Notified
                          </span>
                        )}
                      </div>
                      <Typography
                        className="notification-message notification-clickable"
                        onClick={() =>
                          handleNotificationClick(notification._id, isViewed)
                        }
                      >
                        {notification.notificationMessage}
                      </Typography>
                      <Typography className="notification-time">
                        {formatDate(new Date(notification.createdAt))}
                      </Typography>
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="notifications-pagination">
                  <Pagination
                    current={currentPage}
                    total={totalCount}
                    pageSize={pageSize}
                    onChange={onPageChange}
                    showSizeChanger={false}
                    size="small"
                    simple
                  />
                </div>
              )}
            </>
          ) : (
            <div className="notifications-empty-container">
              <Empty
                description={
                  <Typography className="notifications-empty-text">
                    No notifications found
                  </Typography>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationsDropdown;
